import { FC, ReactNode, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUserStore } from "../../stores/user.store";
import { useActivitySidebar } from "../../hooks/useActivitySidebar";
import {
	useActivitySidebarUiStore,
	ACTIVITY_SIDEBAR_WIDTH,
	APP_BAR_HEIGHT,
} from "../../stores/activitySidebar.store";
import TodaysActivitiesList from "./TodaysActivitiesList";
import DelayedMissedList from "./DelayedMissedList";
import InputSubmittedList from "./InputSubmittedList";

interface SidebarCardProps {
	title: string;
	children: ReactNode;
}

// SIMS-7579 (UI redesign): each section is its own card in the rail (per Figma),
// replacing the previous single-panel-with-dividers layout. The three cards
// share the rail height equally (flex: 1) and each card's body scrolls
// internally so a long list (e.g. Delayed & Missed) doesn't grow the rail or
// crowd out the other cards.
//
// SIMS-7633 (JANUS restyle): white 12px cards on top of the rail's --surface-2
// background. (The per-title divider from the initial restyle was removed as a
// follow-up bugfix — see git history.) Both surfaces
// are theme tokens (theme.palette.surface.light / .main — see
// docs/context/ActivitySidebar.md), never hardcoded hex, so tenants that
// override surfaceLight/surfaceColor still render correctly. The shadow is
// unchanged: MUI Card's default elevation=1 already resolves theme.shadows[1]
// (same convention as KpiCards/KpiCard.tsx).
const SidebarCard: FC<SidebarCardProps> = ({ title, children }) => {
	const theme = useTheme();
	return (
		<Card
			data-testid="sidebar-card"
			sx={{
				backgroundColor: theme.palette.surface.light,
				borderRadius: "12px",
				flex: "1 1 0",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, p: 2, pb: 1 }}>
				{title}
			</Typography>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2 }}>
				{children}
			</Box>
		</Card>
	);
};

// SIMS-4146 + SIMS-7579(UI): the Activity Sidebar. Visible only when the
// coordinator is assigned to a Daily Workflow-enabled program (desktop/tablet).
// On wide screens it opens as a persistent rail that PUSHES the page content
// (the Layout reserves right-margin via the shared `pushing` flag); on narrower
// screens it falls back to a temporary overlay drawer so the page isn't crushed.
//
// SIMS-7638 (JANUS restyle): the toggle is now a single lightbulb control (no
// separate in-rail "Activity" title + chevron collapse button — see
// docs/context/ActivitySidebar.md). Closed = white fill / green (theme
// primary) icon; open = green (theme primary) fill / white icon. In the wide
// (push) layout it docks flush to the right edge of the content column — the
// viewport's right edge when closed, and the rail's left edge (offset by
// ACTIVITY_SIDEBAR_WIDTH) when open, since Main's right edge always sits at
// `viewportRight - (pushing ? ACTIVITY_SIDEBAR_WIDTH : 0)` (see
// src/views/Layout/index.tsx). At the narrower overlay breakpoint it falls
// back to the pre-existing floating position (unchanged, out of scope).
export const ActivitySidebar: FC = () => {
	const theme = useTheme();
	const isDesktopOrTablet = useMediaQuery(theme.breakpoints.up("sm"));
	// Push (persistent) only when there is room; overlay below this.
	const isWide = useMediaQuery(theme.breakpoints.up("lg"));
	const coordinatorId = useUserStore((state) => state.userInfo?.id);
	const { data } = useActivitySidebar(coordinatorId);
	const [open, setOpen] = useState(false);
	const setPushing = useActivitySidebarUiStore((state) => state.setPushing);

	const todaysActivities = data?.sections.todaysActivities.count ?? 0;
	const delayedMissed = data?.sections.delayedMissed.count ?? 0;
	// Defensive `?.` on the newest section: during a rollout the frontend may
	// briefly run against a backend that predates SIMS-4338 and omits it.
	const inputSubmitted = data?.sections.inputSubmitted?.count ?? 0;
	const total = todaysActivities + delayedMissed + inputSubmitted;

	const visible = isDesktopOrTablet && Boolean(data?.programsEnabled);
	// The Layout reserves space only when the rail is open in push mode.
	const pushing = visible && open && isWide;

	useEffect(() => {
		setPushing(pushing);
		return () => setPushing(false);
	}, [pushing, setPushing]);

	if (!visible) {
		return null;
	}

	return (
		<>
			<Fab
				data-testid="activity-sidebar-toggle"
				size="medium"
				onClick={() => setOpen((prev) => !prev)}
				aria-label={
					open ? "Collapse activity sidebar" : "Open activity sidebar"
				}
				sx={{
					position: "fixed",
					zIndex: (t) => t.zIndex.drawer + 1,
					// SIMS-7638 AC1/AC2: closed = white fill / green icon, open =
					// green fill / white icon — both theme tokens, no hardcoded hex.
					backgroundColor: open
						? theme.palette.primary.main
						: theme.palette.surface.light,
					color: open
						? theme.palette.primary.contrastText
						: theme.palette.primary.main,
					"&:hover": {
						backgroundColor: open
							? theme.palette.primary.dark
							: theme.palette.surface.light,
					},
					transition: theme.transitions.create(
						["right", "background-color", "color"],
						{
							easing: theme.transitions.easing.easeOut,
							duration: theme.transitions.duration.enteringScreen,
						},
					),
					...(isWide
						? {
								// SIMS-7638 AC5: docked to the right edge of the content
								// column in the wide/push layout.
								top: "50%",
								transform: "translateY(-50%)",
								right: open ? ACTIVITY_SIDEBAR_WIDTH : 0,
							}
						: {
								// SIMS-7638 AC5: floating fallback at the narrower overlay
								// breakpoint (unchanged from the pre-restyle position).
								right: 16,
								bottom: 24,
							}),
				}}
			>
				<Badge badgeContent={total} color="error">
					<LightbulbIcon />
				</Badge>
			</Fab>
			<Drawer
				anchor="right"
				variant={isWide ? "persistent" : "temporary"}
				open={open}
				onClose={() => setOpen(false)}
				aria-label="Activity sidebar"
				PaperProps={{
					sx: {
						width: ACTIVITY_SIDEBAR_WIDTH,
						boxSizing: "border-box",
						p: 2,
						// Sit below the fixed AppBar (like the left nav).
						top: `${APP_BAR_HEIGHT}px`,
						height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
						backgroundColor: theme.palette.surface.main,
						borderLeft: `1px solid ${theme.palette.divider}`,
						// Fixed-height flex column; the cards scroll, not the rail.
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					},
				}}
			>
				{/* SIMS-7638 AC4: no in-rail title/chevron — the lightbulb above is
				    the sole open/close control. Equal-height cards fill the rail. */}
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<SidebarCard title="Today's activities">
						<TodaysActivitiesList coordinatorId={coordinatorId} />
					</SidebarCard>
					<SidebarCard title="Inputs submitted">
						<InputSubmittedList coordinatorId={coordinatorId} />
					</SidebarCard>
					<SidebarCard title="Delayed and missed activities">
						<DelayedMissedList coordinatorId={coordinatorId} />
					</SidebarCard>
				</Box>
			</Drawer>
		</>
	);
};

export default ActivitySidebar;
